import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, CheckCircle, Mail, Phone, Download, Box, Droplets, Shield, Settings } from 'lucide-react';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import SEOHead from '../components/SEOHead';
import Footer from '../components/Footer';
import FiltrationCTABanner from '../components/FiltrationCTABanner';

const WaterBoxPage = () => {
  const [openFAQ, setOpenFAQ] = useState<number | null>(null);

  const toggleFAQ = (index: number) => {
    setOpenFAQ(openFAQ === index ? null : index);
  };

  const faqs = [
    {
      question: "What is the primary purpose of a Water Box filter?",
      answer: "Water Box filters provide coarse intake screening for ponds, canals, and sumps. They remove large solids (leaves, twigs, debris) before water reaches pumps and pressure filters. The large screen area maintains low intake velocity to prevent screen blinding and reduce maintenance frequency. Water Boxes are commonly installed upstream of BIG or TIG pressure filters for complete two-stage protection."
    },
    {
      question: "How does the float valve assembly work?",
      answer: "The float valve assembly maintains consistent water level inside the Water Box chamber. As water is drawn out by the pump, the float valve opens to allow more water in, ensuring continuous flow and preventing air entrainment. This self-regulating system requires no external controls or power. The float mechanism also prevents overflow during high-water conditions by closing when the chamber is full."
    },
    {
      question: "What materials are Water Box filters available in?",
      answer: "Water Box filters are available in mild steel with epoxy coating (standard, economical option) or 304/316 stainless steel (corrosive environments, marine applications, food-grade requirements). Mild steel with epoxy is suitable for most agricultural irrigation. Stainless steel is recommended for coastal areas, chlorinated water, or applications requiring superior corrosion resistance and longevity."
    },
    {
      question: "How often do Water Box screens require cleaning?",
      answer: "Cleaning frequency depends on water quality and debris loading. Clean water sources: monthly to seasonally. Moderate debris (agricultural canals): weekly to monthly. Heavy organic matter (ponds with algae): daily to weekly. Monitor system pressure or flow rate—clean when reduction is noticeable. Inspection is easy via access panels. Most cleaning takes 15-30 minutes with hose or brush."
    },
    {
      question: "Can I use a Water Box with my existing pump and filtration system?",
      answer: "Yes, Water Box filters are designed for easy integration with existing systems. Match the suction size to your pump inlet and valve size to your piping. Water Boxes install upstream of your pump, protecting both the pump and any downstream filters (BIG, TIG, etc.). Our filtration specialists can help size the appropriate model based on your pump flow rate, water source conditions, and piping configuration."
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <SEOHead
        title="Water Box (WB) Intake Filters | Coarse Screening | CTC Waterworks"
        description="Water Box intake screen systems for ponds, canals, and sumps. Large screen area with float-valved level control for consistent flow. Complete specifications and system integration guidance."
        canonical="https://www.ctcwaterworks.com/filtration/water-intake/water-box"
      />
      <Navbar />
      <FiltrationCTABanner />

      {/* Breadcrumbs */}
      <nav aria-label="Breadcrumb" className="bg-gray-50 border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-6 py-3">
          <ol className="flex items-center space-x-2 text-sm">
            <li>
              <Link to="/" className="text-gray-600 hover:text-ctc-blue">Home</Link>
            </li>
            <li className="text-gray-400">/</li>
            <li>
              <Link to="/filtration" className="text-gray-600 hover:text-ctc-blue">Filtration</Link>
            </li>
            <li className="text-gray-400">/</li>
            <li>
              <Link to="/filtration/water-intake" className="text-gray-600 hover:text-ctc-blue">Water Box & Specialty</Link>
            </li>
            <li className="text-gray-400">/</li>
            <li className="text-gray-900 font-semibold">Water Box</li>
          </ol>
        </div>
      </nav>

      {/* Product Images Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Water Box Product Images</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-white rounded-lg shadow-lg overflow-hidden">
              <img src="/Water Box.jpeg" alt="Water Box Intake Filter - Primary Product" className="w-full h-auto" />
              <div className="p-4">
                <h3 className="font-bold text-gray-900 mb-2">Water Box Filter (Primary)</h3>
                <p className="text-sm text-gray-700">General-purpose intake screening with large screen area and float valve control.</p>
              </div>
            </div>
            <div className="bg-white rounded-lg shadow-lg overflow-hidden">
              <img src="/Water Box 2.jpeg" alt="Water Box Installation View" className="w-full h-auto" />
              <div className="p-4">
                <h3 className="font-bold text-gray-900 mb-2">Water Box Installation</h3>
                <p className="text-sm text-gray-700">Typical field installation showing float valve assembly and piping connections.</p>
              </div>
            </div>
            <div className="bg-white rounded-lg shadow-lg overflow-hidden">
              <img src="/Water Box custom.jpeg" alt="Custom Water Box Configuration" className="w-full h-auto" />
              <div className="p-4">
                <h3 className="font-bold text-gray-900 mb-2">Custom Configuration</h3>
                <p className="text-sm text-gray-700">Custom Water Box with specialized port arrangements for unique installations.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Hero Section */}
      <section className="pt-16 pb-12 bg-gradient-to-br from-blue-50 to-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Main Content - Left 2/3 */}
            <div className="lg:col-span-2">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
              >
                <div className="flex items-center mb-4">
                  <Box className="text-ctc-blue mr-4" size={48} />
                  <div>
                    <h1 className="text-5xl font-bold text-gray-900">
                      Water Box (WB) Intake Filters
                    </h1>
                  </div>
                </div>
                <p className="text-2xl text-gray-600 mb-8 font-light leading-relaxed">
                  General-purpose intake screening for ponds, canals, and sumps.
                </p>

                <div className="bg-white/60 backdrop-blur-sm border border-gray-200 rounded-lg p-6 mb-6">
                  <h3 className="font-bold text-lg mb-3 text-gray-900">About Water Box Systems</h3>
                  <p className="text-gray-700 leading-relaxed">
                    The Water Box is a general-purpose intake screen system for ponds, canals, and sumps. It features a large screen area and float-valved level control to deliver consistent flow to pumps and pressure filters. Commonly installed upstream of BIG or TIG filters for coarse screening.
                  </p>
                </div>
              </motion.div>
            </div>

            {/* Right Rail - Downloads & CTAs */}
            <div className="lg:col-span-1">
              <div className="bg-white rounded-lg shadow-xl p-6 space-y-4 sticky top-6">
                <h3 className="text-xl font-bold text-gray-900 mb-4">Resources & Support</h3>

                <Link
                  to="/contact?topic=filtration"
                  className="block w-full bg-ctc-blue text-white px-6 py-4 rounded-lg font-semibold hover:bg-blue-700 transition-colors text-center shadow-lg"
                >
                  <Mail className="inline-block mr-2 mb-1" size={20} />
                  Talk to a Filtration Specialist
                </Link>

                <div className="space-y-2 pt-4 border-t border-gray-200">
                  <h4 className="font-semibold text-gray-900 text-sm mb-2">Download Resources</h4>

                  <a
                    href="#"
                    className="flex items-center text-sm text-gray-700 hover:text-ctc-blue p-2 rounded hover:bg-gray-50 transition-colors"
                    onClick={(e) => e.preventDefault()}
                  >
                    <Download size={16} className="mr-2 flex-shrink-0" />
                    <span>Water Box Sell Sheet (PDF)</span>
                  </a>

                  <a
                    href="#"
                    className="flex items-center text-sm text-gray-700 hover:text-ctc-blue p-2 rounded hover:bg-gray-50 transition-colors"
                    onClick={(e) => e.preventDefault()}
                  >
                    <Download size={16} className="mr-2 flex-shrink-0" />
                    <span>Dimensional Drawing (PDF)</span>
                  </a>

                  <a
                    href="#"
                    className="flex items-center text-sm text-gray-700 hover:text-ctc-blue p-2 rounded hover:bg-gray-50 transition-colors"
                    onClick={(e) => e.preventDefault()}
                  >
                    <Download size={16} className="mr-2 flex-shrink-0" />
                    <span>Spec Table (CSV)</span>
                  </a>
                </div>

                <div className="pt-4 border-t border-gray-200">
                  <div className="flex items-center space-x-3 text-gray-700 mb-3">
                    <Phone size={20} />
                    <span className="text-sm">(509) 901-7476</span>
                  </div>
                  <div className="flex items-center space-x-3 text-gray-700">
                    <Mail size={20} />
                    <a href="mailto:Andre@ctcwaterworks.com" className="text-sm hover:text-ctc-blue underline">
                      Andre@ctcwaterworks.com
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Value Props Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center">Why Choose Water Box?</h2>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-gradient-to-br from-blue-50 to-white border border-blue-200 rounded-lg p-6">
              <Shield className="text-ctc-blue mb-4" size={36} />
              <h3 className="font-bold text-gray-900 mb-2">Large Screen Area</h3>
              <p className="text-gray-700 text-sm leading-relaxed">
                High surface area maintains low intake velocity, preventing screen blinding and extending time between cleanings. Handles heavy debris loading effectively.
              </p>
            </div>

            <div className="bg-gradient-to-br from-green-50 to-white border border-green-200 rounded-lg p-6">
              <Droplets className="text-green-600 mb-4" size={36} />
              <h3 className="font-bold text-gray-900 mb-2">Float Valve Control</h3>
              <p className="text-gray-700 text-sm leading-relaxed">
                Self-regulating float valve assembly maintains consistent water level and flow. No external controls or power required—simple, reliable operation.
              </p>
            </div>

            <div className="bg-gradient-to-br from-orange-50 to-white border border-orange-200 rounded-lg p-6">
              <Settings className="text-orange-600 mb-4" size={36} />
              <h3 className="font-bold text-gray-900 mb-2">Overflow Protection</h3>
              <p className="text-gray-700 text-sm leading-relaxed">
                Built-in overflow ports prevent chamber overfilling during high-water conditions or float valve malfunction. Safe, fail-tolerant design.
              </p>
            </div>

            <div className="bg-gradient-to-br from-gray-50 to-white border border-gray-300 rounded-lg p-6">
              <Box className="text-gray-700 mb-4" size={36} />
              <h3 className="font-bold text-gray-900 mb-2">System Integration</h3>
              <p className="text-gray-700 text-sm leading-relaxed">
                Works seamlessly with BIG or TIG pressure filters downstream. Water Box handles coarse screening, pressure filter provides fine filtration.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Specifications Table */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Water Box Specifications</h2>
          <p className="text-center text-gray-600 mb-8 max-w-3xl mx-auto">
            Select Water Box model based on pump flow requirements. All dimensions in inches.
          </p>

          <div className="overflow-x-auto bg-white rounded-lg shadow-lg">
            <table className="w-full border-collapse">
              <thead className="bg-gradient-to-r from-ctc-blue to-blue-700 text-white sticky top-0">
                <tr>
                  <th className="px-4 py-3 text-left font-semibold">Flow (GPM)</th>
                  <th className="px-4 py-3 text-left font-semibold">Screen Area (in²)</th>
                  <th className="px-4 py-3 text-left font-semibold">H</th>
                  <th className="px-4 py-3 text-left font-semibold">L</th>
                  <th className="px-4 py-3 text-left font-semibold">W</th>
                  <th className="px-4 py-3 text-left font-semibold">Valve</th>
                  <th className="px-4 py-3 text-left font-semibold">Suction</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                <tr className="hover:bg-blue-50">
                  <td className="px-4 py-3">200</td>
                  <td className="px-4 py-3">748</td>
                  <td className="px-4 py-3">20</td>
                  <td className="px-4 py-3">24</td>
                  <td className="px-4 py-3">26</td>
                  <td className="px-4 py-3">4"</td>
                  <td className="px-4 py-3">3"</td>
                </tr>
                <tr className="hover:bg-blue-50">
                  <td className="px-4 py-3">300</td>
                  <td className="px-4 py-3">1288</td>
                  <td className="px-4 py-3">27</td>
                  <td className="px-4 py-3">30</td>
                  <td className="px-4 py-3">48</td>
                  <td className="px-4 py-3">6"</td>
                  <td className="px-4 py-3">3"</td>
                </tr>
                <tr className="hover:bg-blue-50">
                  <td className="px-4 py-3">400</td>
                  <td className="px-4 py-3">1656</td>
                  <td className="px-4 py-3">27</td>
                  <td className="px-4 py-3">38</td>
                  <td className="px-4 py-3">48</td>
                  <td className="px-4 py-3">8"</td>
                  <td className="px-4 py-3">4"</td>
                </tr>
                <tr className="hover:bg-blue-50">
                  <td className="px-4 py-3">500</td>
                  <td className="px-4 py-3">2088</td>
                  <td className="px-4 py-3">27</td>
                  <td className="px-4 py-3">38</td>
                  <td className="px-4 py-3">60</td>
                  <td className="px-4 py-3">10"</td>
                  <td className="px-4 py-3">4"</td>
                </tr>
                <tr className="hover:bg-blue-50">
                  <td className="px-4 py-3">600</td>
                  <td className="px-4 py-3">2520</td>
                  <td className="px-4 py-3">27</td>
                  <td className="px-4 py-3">38</td>
                  <td className="px-4 py-3">72</td>
                  <td className="px-4 py-3">10"</td>
                  <td className="px-4 py-3">5"</td>
                </tr>
                <tr className="hover:bg-blue-50">
                  <td className="px-4 py-3">700</td>
                  <td className="px-4 py-3">2784</td>
                  <td className="px-4 py-3">27</td>
                  <td className="px-4 py-3">50</td>
                  <td className="px-4 py-3">60</td>
                  <td className="px-4 py-3">10"</td>
                  <td className="px-4 py-3">6"</td>
                </tr>
                <tr className="hover:bg-blue-50">
                  <td className="px-4 py-3">800</td>
                  <td className="px-4 py-3">3360</td>
                  <td className="px-4 py-3">27</td>
                  <td className="px-4 py-3">50</td>
                  <td className="px-4 py-3">72</td>
                  <td className="px-4 py-3">10"</td>
                  <td className="px-4 py-3">6"</td>
                </tr>
                <tr className="hover:bg-blue-50">
                  <td className="px-4 py-3">1000</td>
                  <td className="px-4 py-3">4200</td>
                  <td className="px-4 py-3">27</td>
                  <td className="px-4 py-3">50</td>
                  <td className="px-4 py-3">84</td>
                  <td className="px-4 py-3">12"</td>
                  <td className="px-4 py-3">6"</td>
                </tr>
                <tr className="hover:bg-blue-50">
                  <td className="px-4 py-3">1200</td>
                  <td className="px-4 py-3">5040</td>
                  <td className="px-4 py-3">27</td>
                  <td className="px-4 py-3">60</td>
                  <td className="px-4 py-3">96</td>
                  <td className="px-4 py-3">12"</td>
                  <td className="px-4 py-3">6"</td>
                </tr>
                <tr className="hover:bg-blue-50">
                  <td className="px-4 py-3">2000</td>
                  <td className="px-4 py-3">6540</td>
                  <td className="px-4 py-3">27</td>
                  <td className="px-4 py-3">60</td>
                  <td className="px-4 py-3">109</td>
                  <td className="px-4 py-3">12"</td>
                  <td className="px-4 py-3">6"</td>
                </tr>
                <tr className="hover:bg-blue-50">
                  <td className="px-4 py-3">2400</td>
                  <td className="px-4 py-3">10080</td>
                  <td className="px-4 py-3">36</td>
                  <td className="px-4 py-3">120</td>
                  <td className="px-4 py-3">84</td>
                  <td className="px-4 py-3">(2) 12"</td>
                  <td className="px-4 py-3">(2) 8"</td>
                </tr>
                <tr className="hover:bg-blue-50">
                  <td className="px-4 py-3">3000</td>
                  <td className="px-4 py-3">11520</td>
                  <td className="px-4 py-3">46</td>
                  <td className="px-4 py-3">120</td>
                  <td className="px-4 py-3">96</td>
                  <td className="px-4 py-3">(2) 12"</td>
                  <td className="px-4 py-3">(2) 8"</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="mt-6 bg-blue-50 border-l-4 border-ctc-blue p-6 rounded-r-lg max-w-4xl mx-auto">
            <p className="text-gray-700 leading-relaxed">
              <span className="font-semibold text-gray-900">Materials:</span> Available in mild steel with epoxy coating or 304/316 stainless steel options. All dimensions in inches. H = Height, L = Length, W = Width. Float valve assembly, overflow ports, and large screen surface included with all models. Contact engineering for site-specific sizing and material selection guidance.
            </p>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Key Features</h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-gradient-to-br from-blue-50 to-white border-2 border-blue-300 rounded-lg p-8">
              <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                <Droplets className="mr-3 text-ctc-blue" size={28} />
                Float Valve Assembly
              </h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                Self-regulating float valve maintains consistent water level inside the chamber. As pump draws water out, the valve opens to allow more in, ensuring continuous flow without air entrainment. No external controls or power required.
              </p>
              <ul className="space-y-2">
                <li className="flex items-start">
                  <CheckCircle className="mr-2 text-ctc-blue mt-1 flex-shrink-0" size={18} />
                  <span className="text-sm text-gray-700">Self-regulating level control</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="mr-2 text-ctc-blue mt-1 flex-shrink-0" size={18} />
                  <span className="text-sm text-gray-700">No power or controls needed</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="mr-2 text-ctc-blue mt-1 flex-shrink-0" size={18} />
                  <span className="text-sm text-gray-700">Prevents air entrainment</span>
                </li>
              </ul>
            </div>

            <div className="bg-gradient-to-br from-green-50 to-white border-2 border-green-300 rounded-lg p-8">
              <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                <Shield className="mr-3 text-green-600" size={28} />
                Overflow Ports
              </h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                Built-in overflow ports provide fail-safe protection during high-water conditions or float valve malfunction. Water safely exits chamber before overfilling occurs. Simple, reliable safety feature.
              </p>
              <ul className="space-y-2">
                <li className="flex items-start">
                  <CheckCircle className="mr-2 text-green-600 mt-1 flex-shrink-0" size={18} />
                  <span className="text-sm text-gray-700">High-water protection</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="mr-2 text-green-600 mt-1 flex-shrink-0" size={18} />
                  <span className="text-sm text-gray-700">Float valve backup</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="mr-2 text-green-600 mt-1 flex-shrink-0" size={18} />
                  <span className="text-sm text-gray-700">Fail-tolerant design</span>
                </li>
              </ul>
            </div>
          </div>

          <div className="mt-8 bg-orange-50 border-l-4 border-orange-600 p-6 rounded-r-lg">
            <p className="text-gray-700 leading-relaxed">
              <span className="font-semibold text-gray-900">System Design:</span> Water Box filters are typically installed at the water source (pond, canal, sump) with suction line running to pump. The large screen area and low intake velocity prevent debris accumulation and extend cleaning intervals. Pair with BIG or TIG pressure filters downstream for complete coarse-to-fine filtration protection.
            </p>
          </div>
        </div>
      </section>

      {/* Mesh/Perf Reference */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Mesh & Perforation Reference</h2>

          <div className="overflow-x-auto bg-white rounded-lg shadow-lg">
            <table className="w-full border-collapse">
              <thead className="bg-gradient-to-r from-gray-700 to-gray-800 text-white">
                <tr>
                  <th className="px-4 py-3 text-left font-semibold">Mesh</th>
                  <th className="px-4 py-3 text-left font-semibold">Open Area (%)</th>
                  <th className="px-4 py-3 text-left font-semibold">Equivalent Perf</th>
                  <th className="px-4 py-3 text-left font-semibold">Typical Use</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                <tr className="hover:bg-gray-50">
                  <td className="px-4 py-3">30</td>
                  <td className="px-4 py-3">50</td>
                  <td className="px-4 py-3">0.020–0.025</td>
                  <td className="px-4 py-3">Intake</td>
                </tr>
                <tr className="hover:bg-gray-50">
                  <td className="px-4 py-3">60</td>
                  <td className="px-4 py-3">31</td>
                  <td className="px-4 py-3">0.015–0.020</td>
                  <td className="px-4 py-3">Micro</td>
                </tr>
                <tr className="hover:bg-gray-50">
                  <td className="px-4 py-3">80</td>
                  <td className="px-4 py-3">31</td>
                  <td className="px-4 py-3">0.010–0.012</td>
                  <td className="px-4 py-3">Drip</td>
                </tr>
                <tr className="hover:bg-gray-50">
                  <td className="px-4 py-3">120</td>
                  <td className="px-4 py-3">31</td>
                  <td className="px-4 py-3">0.008–0.010</td>
                  <td className="px-4 py-3">Fine protection</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Cross-Links Section */}
      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center">Complete Your Filtration System</h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Link
              to="/filtration/inline/big"
              className="bg-white border-2 border-green-300 rounded-lg p-6 hover:shadow-xl transition-shadow"
            >
              <h3 className="text-xl font-bold text-gray-900 mb-2">Pair with BIG Series</h3>
              <p className="text-gray-700 mb-4 text-sm leading-relaxed">
                Add downstream BIG inline filters for pressure filtration. Water Box handles coarse screening, BIG provides fine polishing with automatic cleaning.
              </p>
              <div className="flex items-center text-green-600 font-semibold">
                View BIG Series Filters
                <ArrowRight className="ml-2" size={18} />
              </div>
            </Link>

            <Link
              to="/filtration/inline/tig"
              className="bg-white border-2 border-gray-300 rounded-lg p-6 hover:shadow-xl transition-shadow"
            >
              <h3 className="text-xl font-bold text-gray-900 mb-2">Pair with TIG Series</h3>
              <p className="text-gray-700 mb-4 text-sm leading-relaxed">
                Compact true-inline TIG filters for space-constrained installations. Works downstream of Water Box for secondary polishing.
              </p>
              <div className="flex items-center text-gray-700 font-semibold">
                View TIG Series Filters
                <ArrowRight className="ml-2" size={18} />
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Frequently Asked Questions</h2>

          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div key={index} className="bg-white rounded-lg shadow-md overflow-hidden">
                <button
                  onClick={() => toggleFAQ(index)}
                  className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-gray-50 transition-colors"
                >
                  <span className="font-semibold text-gray-900 pr-4">{faq.question}</span>
                  <CheckCircle
                    className={`flex-shrink-0 text-ctc-blue transition-transform ${
                      openFAQ === index ? 'transform rotate-180' : ''
                    }`}
                    size={24}
                  />
                </button>
                {openFAQ === index && (
                  <div className="px-6 pb-4 bg-gray-50">
                    <p className="text-gray-700 leading-relaxed">{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="py-16 bg-gradient-to-br from-ctc-blue to-blue-800 text-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-3xl font-bold mb-4">Ready to Protect Your System with Water Box?</h2>
          <p className="text-xl text-blue-100 mb-8">
            Our filtration specialist team will help you select the right Water Box model, configure valve and suction sizes, and design a complete intake-to-pressure filtration system for your application.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact?topic=filtration"
              className="bg-white text-ctc-blue px-8 py-4 rounded-lg font-semibold hover:bg-gray-100 transition-colors shadow-lg inline-flex items-center justify-center"
            >
              <Mail className="mr-2" size={20} />
              Talk to a Filtration Specialist
            </Link>

            <Link
              to="/filtration/size-my-filter"
              className="bg-blue-800 text-white px-8 py-4 rounded-lg font-semibold hover:bg-blue-900 transition-colors shadow-lg inline-flex items-center justify-center"
            >
              <Settings className="mr-2" size={20} />
              Size My System
            </Link>
          </div>

          <div className="mt-8 pt-8 border-t border-white/20">
            <div className="flex flex-col sm:flex-row items-center justify-center gap-6 text-blue-100">
              <div className="flex items-center">
                <Phone className="mr-2" size={20} />
                <span>(509) 901-7476</span>
              </div>
              <div className="flex items-center">
                <Mail className="mr-2" size={20} />
                <a href="mailto:Andre@ctcwaterworks.com" className="hover:text-white underline">
                  Andre@ctcwaterworks.com
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default WaterBoxPage;
